import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";

/**
 * Floating popup for entering/picking a serial number for a single item.
 * Used when: (a) user picks serial item by name from search, (b) kit has serial items.
 *
 * Props:
 *   itemName       - display name
 *   itemDefinitionId - optional; passed to onAddStockShortcut for Inventory prefill
 *   availableSerials - array of serial strings to pick from
 *   onConfirm(serial) - called when user picks or types a serial that exists in list
 *   onCancel       - called when user closes without picking
 *   onAddStockShortcut({ serialNumber, itemDefinitionId }) - optional jump to Inventory add-stock flow
 */
const SerialInputPopup = ({
  itemName,
  itemDefinitionId = null,
  availableSerials = [],
  onConfirm,
  onCancel,
  onAddStockShortcut = null,
}) => {
  const [term, setTerm] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!term.trim()) return availableSerials;
    return availableSerials.filter((s) =>
      normalizeItemSearchText(s).includes(normalizeItemSearchText(term)),
    );
  }, [availableSerials, term]);

  const trimmed = term.trim();
  const exactInList = useMemo(() => {
    if (!trimmed) return false;
    return availableSerials.some(
      (s) =>
        s === trimmed ||
        normalizeItemSearchText(s) === normalizeItemSearchText(trimmed),
    );
  }, [availableSerials, trimmed]);

  const resolveSerialForConfirm = useCallback(() => {
    if (!trimmed) return null;
    const exact = availableSerials.find((s) => s === trimmed);
    if (exact) return exact;
    const ci = availableSerials.find(
      (s) => normalizeItemSearchText(s) === normalizeItemSearchText(trimmed),
    );
    return ci || null;
  }, [availableSerials, trimmed]);

  const handlePrimaryAdd = useCallback(() => {
    const resolved = resolveSerialForConfirm();
    if (resolved) {
      onConfirm(resolved);
      return;
    }
    if (trimmed && typeof onAddStockShortcut === "function") {
      onAddStockShortcut({
        serialNumber: trimmed,
        itemDefinitionId: itemDefinitionId || "",
      });
      onCancel();
    }
  }, [
    resolveSerialForConfirm,
    trimmed,
    onConfirm,
    onAddStockShortcut,
    onCancel,
    itemDefinitionId,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") handlePrimaryAdd();
      if (e.key === "Escape") onCancel();
    },
    [handlePrimaryAdd, onCancel],
  );

  const showNotInListHint =
    trimmed.length > 0 && filtered.length === 0 && !exactInList;

  return (
    <div className="serial-popup-overlay" onClick={onCancel}>
      <div className="serial-popup" onClick={(e) => e.stopPropagation()}>
        <div className="serial-popup-header">
          <span>בחר מספר סידורי — {itemName}</span>
          <button type="button" className="serial-popup-close" onClick={onCancel}>
            <FaTimes />
          </button>
        </div>
        <input
          ref={inputRef}
          className="serial-popup-input"
          placeholder="הקלד או חפש מספר סידורי..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {filtered.length > 0 && (
          <div className="serial-popup-list">
            {filtered.map((s) => (
              <div
                key={s}
                className="serial-popup-option"
                onClick={() => onConfirm(s)}
              >
                {s}
              </div>
            ))}
          </div>
        )}
        {showNotInListHint && (
          <div className="signature-serial-popup-missing">
            <p>מספר זה לא מופיע ברשימת הסידורים הזמינים (למשל כבר משויך או לא במלאי).</p>
            {typeof onAddStockShortcut === "function" && (
              <button
                type="button"
                className="btn btn-primary btn-sm signature-serial-popup-missing-btn"
                onClick={() => {
                  onAddStockShortcut({
                    serialNumber: trimmed,
                    itemDefinitionId: itemDefinitionId || "",
                  });
                  onCancel();
                }}
              >
                הוסף למלאי (מקיצור דרך)
              </button>
            )}
            {typeof onAddStockShortcut !== "function" && (
              <p className="signature-missing-serial-child-hint">
                פנה למנהל להוספת המספר במלאי.
              </p>
            )}
          </div>
        )}
        <div className="serial-popup-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            ביטול
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePrimaryAdd}
            disabled={
              !trimmed ||
              (!resolveSerialForConfirm() &&
                typeof onAddStockShortcut !== "function")
            }
          >
            {resolveSerialForConfirm()
              ? "הוסף"
              : typeof onAddStockShortcut === "function"
                ? "הוסף למלאי"
                : "בחר מהרשימה"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerialInputPopup;
